import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  arrow,
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useInteractions,
  useTransitionStyles,
  FloatingArrow,
  FloatingFocusManager,
  FloatingOverlay,
  useId
} from '@floating-ui/react';

import Headline from '@components/atoms/headline/headline';
import CloseIcon from '@components/icons/close/close';

import StyledPopover from './popover.style';

const Popover = ({
  headline,
  trigger,
  closeLabel,
  children,
  placement,
  offsetX = 0,
  isModal = true,
  open,
  onOpen,
  onClose
}) => {
  const arrowRef = useRef(null);
  const popoverWrapper = useRef(null);

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open,
    onOpenChange: (isOpen, e, reason) => {
      if (reason === 'outside-press' || reason === 'escape-key') {
        if (onClose) onClose();
      }

      onOpen(isOpen);
    },
    placement,
    middleware: [
      offset({ mainAxis: 30, crossAxis: offsetX }),
      flip({ fallbackAxisSideDirection: 'none' }),
      shift({ padding: 15 }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  const ARROW_WIDTH = 49;
  const ARROW_HEIGHT = 18; // Size of the arrow inside the square..
  const arrowX = middlewareData.arrow?.x ?? 0;
  const transformX = arrowX + ARROW_WIDTH / 2;

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      transform: 'scale(0)'
    },
    open: {
      transform: 'scale(1)'
    },
    common: {
      transformOrigin: `${transformX}px calc(100% + ${ARROW_HEIGHT}px)`
    }
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const headingId = useId();

  useEffect(() => {
    if (!popoverWrapper.current) return;
    if (open) {
      setTimeout(() => {
        popoverWrapper.current.scrollTo(0, 0);
      }, 100);
    }
  }, [popoverWrapper, open]);

  return (
    <StyledPopover>
      <button
        className={`wishes__button ${trigger.isCircle ? 'wishes__button--circle' : ''}`}
        type="button"
        aria-label={trigger.ariaLabel}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {trigger.icon ? trigger.icon : null}
        <Headline tag="span" main>
          {trigger.label}
        </Headline>
      </button>
      {isMounted && (
        <FloatingFocusManager context={context} modal={isModal}>
          <FloatingOverlay lockScroll />
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            aria-labelledby={headingId}
            {...getFloatingProps()}
          >
            <div className="popover__panel" style={styles}>
              <div className="popover__wrapper" ref={popoverWrapper}>
                <div className="popover__header">
                  <Headline id={headingId} tag="h2" main>
                    {headline}
                  </Headline>
                  <button
                    type="button"
                    className="popover__close"
                    aria-label={closeLabel}
                    onClick={() => {
                      onOpen(false);
                      if (onClose) onClose();
                    }}
                  >
                    <CloseIcon />
                  </button>
                </div>
                <div className="popover__content">{children}</div>
              </div>
              <FloatingArrow
                ref={arrowRef}
                context={context}
                stroke="#b2b2b2"
                strokeWidth={2}
                width={49}
                style={{ transform: 'translateY(2px)' }}
                d="M46.5703 49C42.7596 48.8615 38.7631 42.3869 36.1403 38.1377C35.6733 37.3811 35.2498 36.6951 34.8787 36.128C30.3753 29.2456 18.8287 29.1566 14.2506 36.0026C13.8395 36.6173 13.3667 37.3758 12.8435 38.215C10.1782 42.4906 6.20592 48.8626 2.43422 49L46.5703 49Z"
              />
            </div>
          </div>
        </FloatingFocusManager>
      )}
    </StyledPopover>
  );
};

Popover.propTypes = {
  headline: PropTypes.string.isRequired,
  trigger: PropTypes.shape({
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    isCircle: PropTypes.bool,
    icon: PropTypes.string
  }).isRequired,
  closeLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  placement: PropTypes.string.isRequired,
  offsetX: PropTypes.number,
  isModal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func
};

export default Popover;
